import { useState, useEffect } from "react";
import {
    Button,
    Col,
    Form,
    FormGroup,
    FormFeedback,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "reactstrap";
import Select from "react-select";
import makeAnimated from "react-select/animated";

import type {
    IEmailRule,
    IOption,
    IGroupedOption,
    IModal,
} from "Pages/Tools/EmailRules/EmailRules";
import Slider from "components/Slider/Slider";
import styles from "Pages/Tools/EmailRules/Components/AddOrEditModal/AddOrEditModal.module.css";

interface IProps {
    editRules: (rule: IEmailRule, action: string) => Promise<void>;
    emailRules: IEmailRule[];
    modal: IModal;
    onSetModal: (modal: IModal) => void;
    integrationOptions: IGroupedOption[];
    userOptions: IOption[];
    selectedRule: IEmailRule | undefined;
    onSetSelectedRule: (rule?: IEmailRule) => void;
}

interface IFormData {
    name: string;
    threshhold: string;
    email_on_any_actions: boolean;
    enabled: boolean;
    integrations: IOption[];
    users: string;
    cc_users: string;
}

interface IErrors {
    name: string;
    threshhold: string;
    integrations: string;
    users: string;
    cc_users: string;
}

export default function AddModal({
    editRules,
    emailRules,
    modal,
    onSetModal,
    integrationOptions,
    userOptions,
    selectedRule,
    onSetSelectedRule,
}: IProps) {
    const defaultRule = {
        name: "",
        threshhold: "",
        email_on_any_actions: false,
        enabled: true,
        integrations: [],
        users: "",
        cc_users: "",
    };
    const [formData, setFormData] = useState<IFormData>(defaultRule);
    const defaultErrors = {
        name: "",
        threshhold: "",
        integrations: "",
        users: "",
        cc_users: "",
    };
    const [errors, setErrors] = useState(defaultErrors);

    /** Hook for react-select inputs to make them animated when deleting */
    const animated = makeAnimated();

    // Populate the form with data once the state change hits
    useEffect(() => {
        if (selectedRule) {
            const formatted = formatRuleForForm(selectedRule);
            setFormData(formatted);

            function formatRuleForForm(rule: IEmailRule) {
                return {
                    name: rule.name,
                    threshhold: rule.threshhold,
                    email_on_any_actions: rule.email_on_any_actions,
                    enabled: rule.enabled,
                    integrations: rule.integrations
                        .split(",")
                        .filter((i) => i !== "")
                        .map((i) => ({
                            value: i,
                            label: i === "%" ? "All integrations" : i,
                        })),
                    users: rule.users,
                    cc_users: rule.cc_users,
                };
            }
        }
    }, [selectedRule]);

    const toggle = () => {
        onSetModal("");
        onSetSelectedRule();
        setErrors(defaultErrors);
        setFormData(defaultRule);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { id, value, checked } = e.target;
        const newValue = e.target.type === "checkbox" ? checked : value;
        setFormData({ ...formData, [id]: newValue });
    };

    /** Separare onChange handler, because the react-select inputs work
     * differently and return the selected options instead of a change event. Ex: [{value: "1", label: "Integration 1"}, ...]
     */
    const handleSelectChange = (newValue: any, key: string) => {
        setFormData({ ...formData, [key]: newValue });
    };

    /** Calculates styles to pass to react-select input, because it is a pain
     * and does not work with Reactstrap's FormFeedback
     */
    const selectStyles = (id: keyof IErrors) => {
        return {
            control: (provided: any, state: any) => ({
                ...provided,
                borderColor: state.isFocused
                    ? errors[id] !== ""
                        ? "#dc3545"
                        : "#80bdff"
                    : errors[id] !== ""
                    ? "#dc3545"
                    : "#ced4da",
                boxShadow: state.isFocused
                    ? errors[id] !== ""
                        ? "0 0 0 0.2rem rgba(220, 53, 69, .25)"
                        : "0 0 0 0.2rem rgba(0,123,255,.25)"
                    : "none",
                "&:hover": {
                    borderColor: state.isFocused
                        ? errors[id] !== ""
                            ? "#dc3545"
                            : "#80bdff"
                        : errors[id] !== ""
                        ? "#dc3545"
                        : "#ced4da",
                },
            }),
        };
    };

    /** Performs validation on form submit */
    const validate = (values: IFormData) => {
        let newErrors = { ...errors }; // clone previous errors

        const existingNames = emailRules.map((r) => r.name.toLowerCase());

        // Name
        if (!values.name) {
            newErrors.name = "Required";
        } else if (values.name.length > 50) {
            newErrors.name = "Must be 50 characters or less";
            // If name already exists and there is no selected rule (not editing), don't allow duplicate name
        } else if (
            existingNames.includes(values.name.toLowerCase()) &&
            !selectedRule
        ) {
            newErrors.name = "Name already exists";
        } else {
            newErrors.name = "";
        }

        // Threshhold
        if (!values.threshhold) {
            newErrors.threshhold = "Required";
        } else {
            newErrors.threshhold = "";
        }

        // Integrations
        if (values.integrations.length === 0) {
            newErrors.integrations = "At least one integration is required";
        } else {
            newErrors.integrations = "";
        }

        // Users
        if (values.users.length === 0) {
            newErrors.users = "At least one user is required";
        } else {
            newErrors.users = "";
        }
        setErrors(newErrors);

        if (Object.values(newErrors).some((e) => e !== "")) {
            return false;
        } else {
            return true;
        }
    };

    const handleSubmit = async () => {
        const validated = validate(formData);

        if (!validated) return;

        const rule = {
            name: formData.name,
            threshhold: formData.threshhold,
            email_on_any_actions: formData.email_on_any_actions,
            enabled: formData.enabled,
            // Mapping the next 3 from react-select's format IOption[] { value: string, label: string }
            // What is stored in the database: a string "1,2,3"
            integrations: formData.integrations.map((i) => i.value).join(","),
            users: formData.users,
            cc_users: formData.cc_users,
        };

        const action = selectedRule ? "edit" : "add";

        console.log(
            `${action[0].toUpperCase() + action.slice(1)}ing email rule data: `,
            rule
        );

        editRules(rule, action);
    };

    return (
        <Modal isOpen={modal === "add" || modal === "edit"} toggle={toggle}>
            <ModalHeader toggle={toggle}>
                {selectedRule ? "Edit" : "Add"} Email Rule
            </ModalHeader>
            <ModalBody>
                <Form>
                    {/* Name */}
                    <FormGroup row className={styles.formGroup}>
                        <Col sm={3}>
                            <b>
                                <Label for="name" className={styles.label}>
                                    Name:
                                </Label>
                            </b>
                        </Col>
                        <Col sm={9}>
                            <Input
                                type="text"
                                id="name"
                                value={formData.name}
                                onChange={handleChange}
                                invalid={errors.name !== ""}
                            />
                            {errors.name !== "" && (
                                <FormFeedback>{errors.name}</FormFeedback>
                            )}
                        </Col>
                    </FormGroup>

                    {/* Enabled */}
                    <FormGroup row className={styles.formGroup}>
                        <Col sm={3}>
                            <b>
                                <Label className={styles.label}>Enabled:</Label>
                            </b>
                        </Col>
                        <Col sm={9}>
                            <Slider
                                checked={formData.enabled}
                                onChange={handleChange}
                                id="enabled"
                            />
                        </Col>
                    </FormGroup>

                    {/* Email on any actions */}
                    <FormGroup row className={styles.formGroup}>
                        <Col sm={3}>
                            <b>
                                <Label className={styles.label}>
                                    Email on any action:
                                </Label>
                            </b>
                        </Col>
                        <Col sm={9}>
                            <Slider
                                checked={formData.email_on_any_actions}
                                onChange={handleChange}
                                id="email_on_any_actions"
                            />
                        </Col>
                    </FormGroup>

                    {/* Threshhold */}
                    <FormGroup row className={styles.formGroup}>
                        <Col sm={3}>
                            <b>
                                <Label
                                    for="threshhold"
                                    className={styles.label}
                                >
                                    Threshold:
                                </Label>
                            </b>
                        </Col>
                        <Col sm={9}>
                            <Input
                                type="select"
                                id="threshhold"
                                // Yes, the database column is spelled incorrectly
                                value={formData.threshhold}
                                onChange={handleChange}
                                invalid={errors.threshhold !== ""}
                            >
                                <option value="" disabled>
                                    Select...
                                </option>
                                <option value="Complete">Complete</option>
                                <option value="Warning">Warning</option>
                                <option value="Error">Errored</option>
                            </Input>
                            {errors.threshhold !== "" && (
                                <FormFeedback>{errors.threshhold}</FormFeedback>
                            )}
                        </Col>
                    </FormGroup>

                    {/* Integrations */}
                    <FormGroup row className={styles.formGroup}>
                        <Col sm={3}>
                            <b>
                                <Label
                                    for="integrations"
                                    className={styles.label}
                                >
                                    Integrations:
                                </Label>
                            </b>
                        </Col>
                        <Col sm={9}>
                            <Select
                                classNamePrefix="rs"
                                id="integrations"
                                closeMenuOnSelect={false}
                                components={animated}
                                isMulti
                                value={formData.integrations}
                                options={integrationOptions}
                                onChange={(e) =>
                                    handleSelectChange(e, "integrations")
                                }
                                styles={selectStyles("integrations")} // react-select doesn't work with bootstrap's form validation
                            />
                            {errors.integrations !== "" && (
                                <div className="invalid-feedback d-block">
                                    {errors.integrations}
                                </div>
                            )}
                        </Col>
                    </FormGroup>

                    {/* Users */}
                    <FormGroup row className={styles.formGroup}>
                        <Col sm={3}>
                            <b>
                                <Label for="users" className={styles.label}>
                                    Users:
                                </Label>
                            </b>
                        </Col>
                        <Col sm={9}>
                            <Input
                                id="users"
                                value={formData.users}
                                onChange={handleChange}
                                styles={selectStyles("users")} // react-select doesn't work with bootstrap's form validation
                            />
                            {errors.users !== "" && (
                                <div className="invalid-feedback d-block">
                                    {errors.users}
                                </div>
                            )}
                        </Col>
                    </FormGroup>

                    {/* CC'd Users */}
                    <FormGroup row className={styles.formGroup}>
                        <Col sm={3}>
                            <b>
                                <Label for="cc_users" className={styles.label}>
                                    CC'd Users:
                                </Label>
                            </b>
                        </Col>
                        <Col sm={9}>
                            <Input
                                id="cc_users"
                                value={formData.cc_users}
                                onChange={handleChange}
                                styles={selectStyles("cc_users")} // react-select doesn't work with bootstrap's form validation
                            />
                        </Col>
                    </FormGroup>
                </Form>
            </ModalBody>

            <ModalFooter>
                <Button color="primary" onClick={handleSubmit}>
                    Save
                </Button>{" "}
            </ModalFooter>
        </Modal>
    );
}
